import { BigNumber as NS } from 'bignumber.js';
import { synchronizeLimitBidsAndPositions } from './helpers';
import { resolveActionTypeOnPositions } from '../positions/helpers';
import $store from '@/store/index';
import * as Sentry from '@sentry/vue';
import { ItemCloseRecordSource } from '../../utils/itemClose';
var BinanceAccountUpdateCauseTypes;
(function (BinanceAccountUpdateCauseTypes) {
    BinanceAccountUpdateCauseTypes["deposit"] = "DEPOSIT";
    BinanceAccountUpdateCauseTypes["withdraw"] = "WITHDRAW";
    BinanceAccountUpdateCauseTypes["order"] = "ORDER";
    BinanceAccountUpdateCauseTypes["autoExchange"] = "AUTO_EXCHANGE";
    BinanceAccountUpdateCauseTypes["insuranceClear"] = "INSURANCE_CLEAR";
})(BinanceAccountUpdateCauseTypes || (BinanceAccountUpdateCauseTypes = {}));
var BinanceOrderTypes;
(function (BinanceOrderTypes) {
    BinanceOrderTypes["market"] = "MARKET";
    BinanceOrderTypes["limit"] = "LIMIT";
    BinanceOrderTypes["stopLoss"] = "STOP";
    BinanceOrderTypes["takeProfit"] = "TAKE_PROFIT";
    BinanceOrderTypes["stopLossMarket"] = "STOP_MARKET";
    BinanceOrderTypes["takeProfitMarket"] = "TAKE_PROFIT_MARKET";
    BinanceOrderTypes["liquidation"] = "LIQUIDATION";
})(BinanceOrderTypes || (BinanceOrderTypes = {}));
const isStopLossTakeProfitType = (type) => {
    const stopLossTakeProfitTypes = [
        BinanceOrderTypes.stopLoss,
        BinanceOrderTypes.takeProfit,
        BinanceOrderTypes.takeProfitMarket,
        BinanceOrderTypes.stopLossMarket
    ];
    return stopLossTakeProfitTypes.includes(type);
};
var BinanceExecutionTypes;
(function (BinanceExecutionTypes) {
    BinanceExecutionTypes["new"] = "NEW";
    BinanceExecutionTypes["canceled"] = "CANCELED";
    BinanceExecutionTypes["calculated"] = "CALCULATED";
    BinanceExecutionTypes["expired"] = "EXPIRED";
    BinanceExecutionTypes["trade"] = "TRADE";
})(BinanceExecutionTypes || (BinanceExecutionTypes = {}));
var BinanceOrderStatuses;
(function (BinanceOrderStatuses) {
    BinanceOrderStatuses["new"] = "NEW";
    BinanceOrderStatuses["partiallyFilled"] = "PARTIALLY_FILLED";
    BinanceOrderStatuses["filled"] = "FILLED";
    BinanceOrderStatuses["canceled"] = "CANCELED";
    BinanceOrderStatuses["expired"] = "EXPIRED";
    BinanceOrderStatuses["newInsurance"] = "NEW_INSURANCE";
    BinanceOrderStatuses["newADL"] = "NEW_ADL";
})(BinanceOrderStatuses || (BinanceOrderStatuses = {}));
var BinanceTransferSides;
(function (BinanceTransferSides) {
    BinanceTransferSides["buy"] = "BUY";
    BinanceTransferSides["sell"] = "SELL";
})(BinanceTransferSides || (BinanceTransferSides = {}));
var TransferSides;
(function (TransferSides) {
    TransferSides["long"] = "\u041B\u043E\u043D\u0433";
    TransferSides["short"] = "\u0428\u043E\u0440\u0442";
    TransferSides["stopLoss"] = "SL";
    TransferSides["takeProfit"] = "TP";
})(TransferSides || (TransferSides = {}));
var Sections;
(function (Sections) {
    Sections["spot"] = "\u0421\u043F\u043E\u0442";
    Sections["futures"] = "\u0424\u044C\u044E\u0447\u0435\u0440\u0441";
})(Sections || (Sections = {}));
const depositWithdraws = {};
const getOrderDirection = (side, type) => {
    switch (type) {
        case BinanceOrderTypes.stopLossMarket: {
            return TransferSides.stopLoss;
        }
        case BinanceOrderTypes.takeProfitMarket: {
            return TransferSides.takeProfit;
        }
        default: {
            return side === BinanceTransferSides.buy ? TransferSides.long : TransferSides.short;
        }
    }
};
function handleOrderUpdate(commit, getters, dispatch, data) {
    const p = (str) => parseFloat(String(str));
    const d = data.o;
    const items = getters.getLimitsBidsTable.items;
    const isStopLossTakeProfit = isStopLossTakeProfitType(d.o);
    const isNewOrder = d.X === BinanceOrderStatuses.new && d.x === BinanceExecutionTypes.new;
    const isEmptyOrder = NS(d.q).isZero();
    if (isNewOrder && (!isEmptyOrder || isStopLossTakeProfit)) {
        const body = {
            ticker: d.s,
            volume: NS(d.q).abs(),
            positionPrice: isStopLossTakeProfit ? NS(d.sp) : NS(d.p),
            direction: getOrderDirection(d.S, d.o),
            section: Sections.futures,
            id: d.i,
            closing: false
        };
        commit('pushItemInLimits', { data: body, getters });
    }
    const isCompletedOrder = d.X === BinanceOrderStatuses.filled && d.x === BinanceExecutionTypes.trade;
    if (isCompletedOrder) {
        dispatch('setOrderClose', { by: ItemCloseRecordSource.binance, id: d.i });
        const body = {
            ticker: d.s,
            positionPrice: NS(d.ap),
            volume: NS(d.q).abs(),
            currentPrice: NS(d.p),
            result: NS(0),
            direction: d.S === 'BUY' ? TransferSides.long : TransferSides.short,
            closing: false
        };
        if (body.currentPrice.isZero()) {
            // @ts-ignore
            Sentry.captureException(new Error('CP0 handleOrderUpdate'), { extra: { item: d } });
        }
        const resolvedAction = resolveActionTypeOnPositions(getters.getPositionsTable.items, body);
        let resolvedPayload;
        if (resolvedAction === 'pushItemInPosiions') {
            resolvedPayload = body;
            dispatch('subscribeOnTickerMarketPriceChange', body.ticker);
        }
        else if (resolvedAction === 'updateItemInPositions') {
            const positionsItem = getters.getPositionsTable.items.find((item) => item.ticker === body.ticker);
            let totalQuantity = NS(0);
            if (positionsItem.direction === body.direction) {
                totalQuantity = positionsItem.volume.plus(body.volume);
            }
            else
                totalQuantity = positionsItem.volume.minus(body.volume);
            const currPositionPrice = NS(positionsItem.positionPrice).times(NS(positionsItem.volume))
                .plus(body.positionPrice.times(body.volume))
                .div(NS(positionsItem.volume).plus(body.volume));
            const payloadData = [
                { name: 'volume', value: totalQuantity.abs() },
                { name: 'direction', value: totalQuantity.gt(NS(0)) ? positionsItem.direction : body.direction },
                { name: 'currentPrice', value: body.currentPrice },
                { name: 'positionPrice', value: currPositionPrice.toFixed(4, 1) }
            ];
            resolvedPayload = {
                ticker: body.ticker,
                data: payloadData
            };
            setTimeout(() => {
                synchronizeLimitBidsAndPositions(commit);
            }, 500);
        }
        if (resolvedAction !== 'removeItemInPositions') {
            commit(resolvedAction, resolvedPayload);
        }
        else {
            dispatch('unsubscribeOnTickerMarketPriceChange', body.ticker);
            setTimeout(() => {
                synchronizeLimitBidsAndPositions(commit);
            }, 500);
            dispatch('setPositionClose', { by: ItemCloseRecordSource.binance, ticker: body.ticker });
        }
    }
    const isCanceledOrder = d.X === BinanceOrderStatuses.canceled && d.x === BinanceExecutionTypes.canceled;
    const isExpiredStopLossTakeProfit = isStopLossTakeProfit && d.X === BinanceOrderStatuses.expired && d.x === BinanceOrderStatuses.expired;
    if (isCanceledOrder || isExpiredStopLossTakeProfit) {
        dispatch('setOrderClose', { by: ItemCloseRecordSource.binance, id: d.i });
    }
    const hasTradeOccurred = localStorage.getItem('tradeOccurred');
    if (!hasTradeOccurred) {
        try {
            // window.gtag('event', 'conversion_event_purchase')
            window._tmr.push({ type: 'reachGoal', id: 3467065, goal: 'FirstTimeDepositRequest' });
        }
        catch (e) {
            console.warn(e);
        }
        window.ym(95601145, 'reachGoal', 'FirstTimeDepositRequest');
        localStorage.setItem('tradeOccurred', 'true');
    }
}
export function handleFuturesOrderUpdate(commit, getters, dispatch, data) {
    if (data.o.s.includes('USDT') || data.o.s.includes('USDC') || data.o.s.includes('BTC'))
        handleOrderUpdate(commit, getters, dispatch, data);
}
export function handleFuturesTickerChangeMarketPrice(commit, getters, data) {
    const list = getters.getPositionsTable.items;
    const findedIndex = list.findIndex((positionsItem) => positionsItem.ticker === data.s);
    if (findedIndex === -1) {
        return;
    }
    const item = list[findedIndex];
    const currentPrice = NS(data.p);
    const dirCoef = item.direction === TransferSides.long ? NS(1) : NS(-1);
    // const pF = (num: number | string): number => parseFloat(num + '')
    const result = NS(1).div(item.positionPrice).minus(NS(1).div(currentPrice))
        .times(dirCoef.times(NS(item.positionPrice).times(NS(item.volume))))
        .times(currentPrice);
    // return ((1 / pF(item.positionPrice) - 1 / pF(item.currentPrice)) * (dirCoef * pF(item.positionPrice) * pF(item.volume))) * pF(item.currentPrice)
    // todo проверить правильность интерпретации формулы
    if (result.isNaN()) {
        // @ts-ignore
        Sentry.captureException(new Error('NaN handleFuturesTickerChangeMarketPrice'), { extra: { item: item, payload: data } });
    }
    if (currentPrice.isZero()) {
        // @ts-ignore
        Sentry.captureException(new Error('CP0 handleFuturesTickerChangeMarketPrice'), { extra: { item: item, payload: data } });
    }
    commit('updateItemInPositions', {
        ticker: data.s,
        data: [
            { name: 'currentPrice', value: currentPrice },
            { name: 'result', value: result }
        ]
    });
}
export function updateAvailableInnerTransferBalance() {
    this.$store.dispatch('mainPageModules/loadSpotData', true);
    this.$store.dispatch('mainPageModules/loadFuturesData', true);
}
function autoExchangeHandler(pair, changedItems) {
    const positiveBalanceItem = changedItems.find((item) => {
        const asset = item.a === 'USDT' || item.a === 'USDC' || item.a === 'BTC';
        if (asset && Number(item.bc) > 0)
            return item;
    });
    let baseAsset = '';
    let walletChange = '';
    let alertContent = 'При ликвидации позиции недостаточно баланса, часть активов на фьючерсном счёте была конвертирована.';
    if (positiveBalanceItem) {
        baseAsset = positiveBalanceItem.a;
        walletChange = positiveBalanceItem.bc;
        alertContent = `При ликвидации позиции ${pair} недостаточно баланса ${baseAsset}, часть активов на фьючерсном счёте была конвертирована в ${baseAsset} (${walletChange} ${baseAsset}).`;
    }
    $store.dispatch('alertsBus/openAlert', {
        title: '',
        type: 'info',
        autoDelete: false,
        onlyContent: true,
        content: alertContent
    });
}
export function handleAccountUpdateFutures(commit, data, context) {
    var _a;
    const d = data;
    const changedItems = (_a = d.a) === null || _a === void 0 ? void 0 : _a.B;
    let ignoreUpdateFinres = false;
    if (Array.isArray(changedItems)) {
        switch (d.a.m) {
            case (BinanceAccountUpdateCauseTypes.insuranceClear):
                $store.dispatch('alertsBus/openAlert', {
                    title: 'Произошла ликвидация',
                    type: 'warning',
                    autoDelete: false,
                    content: 'Биржа в принудительном порядке закрыла Ваши позиции'
                }, { root: true });
                break;
            case (BinanceAccountUpdateCauseTypes.autoExchange):
                autoExchangeHandler('', changedItems);
                break;
            case (BinanceAccountUpdateCauseTypes.deposit):
                d.a.B.forEach((item) => {
                    if (depositWithdraws[item.a]) {
                        depositWithdraws[item.a] = depositWithdraws[item.a].plus(NS(item.bc));
                    }
                    else {
                        depositWithdraws[item.a] = NS(item.bc);
                    }
                });
                setTimeout(() => {
                    $store.dispatch('mainPageModules/loadFuturesData');
                    $store.dispatch('mainPageModules/loadSpotData');
                }, 600);
                break;
            case (BinanceAccountUpdateCauseTypes.withdraw):
                d.a.B.forEach((item) => {
                    if (depositWithdraws[item.a]) {
                        depositWithdraws[item.a] = depositWithdraws[item.a].plus(NS(item.bc));
                    }
                    else {
                        depositWithdraws[item.a] = NS(item.bc);
                    }
                });
                ignoreUpdateFinres = true;
                setTimeout(() => {
                    $store.dispatch('mainPageModules/loadFuturesData');
                    $store.dispatch('mainPageModules/loadSpotData');
                }, 600);
                break;
            case (BinanceAccountUpdateCauseTypes.order):
                d.a.B.forEach((item) => {
                    var _a;
                    const payload = {
                        asset: item.a,
                        value: NS(item.wb),
                        innerTransfers: (_a = depositWithdraws[item.a]) !== null && _a !== void 0 ? _a : NS(0),
                        ignoreUpdateFinres
                    };
                    if (depositWithdraws[item.a])
                        payload.innerTransfers = depositWithdraws[item.a];
                    commit('changeDepositFutures', payload);
                });
                ignoreUpdateFinres = true;
                break;
            default:
                break;
        }
        changedItems.forEach((changedItem) => {
            var _a;
            const payload = {
                asset: changedItem.a,
                value: NS(changedItem.wb),
                ignoreUpdateFinres
            };
            payload.innerTransfers = (_a = depositWithdraws[changedItem.a]) !== null && _a !== void 0 ? _a : NS(0);
            commit('changeDepositFutures', payload);
            if (changedItem.a === 'USDT' || changedItem.a === 'USDC' || changedItem.a === 'BTC') {
                commit('updateAvailableDepositFutures', { asset: changedItem.a.toLowerCase(), value: NS(changedItem.bc) });
            }
        });
    }
}
export function resetWithdraws() {
    for (const assetName in depositWithdraws) {
        depositWithdraws[assetName] = NS(0);
    }
}
